import { useFormik } from "formik";
import debounce from "lodash.debounce";
import React, { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import * as Yup from "yup";
import {
  CREDIT_NOTE,
  DEBIT_NOTE,
  DEBOUNCE_TIME,
  DROPDOWN_DEFAULT_LIMIT,
  INVOICE_DOCUMENT,
  NORMAL_INVOICE,
  SALES_NOTE,
} from "../../../helpers/constants";

import {
  getColumnDetail,
  getCreditNoteItemsData,
  getFormattedDocumentLines,
  getReferenceDocumentLabel,
  onKeyPressForm,
  scrollToFirstFormikError,
} from "../../../helpers/utils";
import { getInvoices } from "../../../store/invoicing/actions";
import { SET_MODAL_DOCUMENTS } from "../../../store/invoicing/actionTypes";
import CustomButton from "../../common/CustomButton";
import CustomSearchInput from "../../common/CustomSearchInput";
import Loader from "../../common/Loader";
import NoRecords from "../../common/NoRecords";

const CreditNoteDocumentLineModal = ({
                                       selectedLots,
                                       selectedCustomer,
                                       addDocumentLine,
                                       isModalOpen,
                                       documentType,
                                       handleModalClose,
                                       t,
                                     }) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const INVOICES_OPTIONS = useSelector((state) => state?.Invoicing?.modal?.data);

  const [saveLoader] = useState(false);
  const [tableLoader] = useState(false);
  const [isAllSelected, setIsAllSelected] = useState();
  const [selectedRows, setSelectedRows] = useState();
  const [documentLines, setDocumentLines] = useState([]);
  const [itemsPayload, setItemsPayload] = useState({});
  const [invoiceSearchLoader, setInvoiceSearchLoader] = useState(false);
  const [invoicePayload, setInvoicePayload] = useState({
    page: 1,
    limit: DROPDOWN_DEFAULT_LIMIT,
    // sort: "document_no",
    is_credit_note: false,
    filter: {
      customer: selectedCustomer,
      document_type__description__in: [CREDIT_NOTE, DEBIT_NOTE].includes(documentType)
        ? [NORMAL_INVOICE, SALES_NOTE]
        : [INVOICE_DOCUMENT],
    },
    exclude: {
      id: id,
      is_cancel: true,
    },
  });

  useEffect(() => {
    setInvoicePayload((invoicePayload) => {
      invoicePayload.filter.document_type__description__in = [CREDIT_NOTE, DEBIT_NOTE].includes(documentType)
        ? [NORMAL_INVOICE, SALES_NOTE]
        : [INVOICE_DOCUMENT];
      return { ...invoicePayload };
    });
  }, [documentType]);

  // to handle the invoice search
  const handleInvoiceSearch = (value) => {
    setInvoicePayload((prevState) => ({
      ...prevState,
      filter: { ...prevState?.filter, search_document__icontains: value },
    }));
  };
  // debounce for invoice search
  const invoiceSearchDebounce = useMemo(() => {
    return debounce(handleInvoiceSearch, DEBOUNCE_TIME);
  }, []);

  useEffect(() => {
    // To reset the modal documents when the modal is closed
    return () => {
      dispatch({
        type: SET_MODAL_DOCUMENTS,
        payload: {
          data: [],
          total: 0,
        },
      });
    };
  }, []);

  // to clear debounce
  useEffect(() => {
    return () => {
      invoiceSearchDebounce.cancel();
    };
  }, [invoiceSearchDebounce]);

  // to get the list of invoices
  useEffect(() => {
    if (isModalOpen) {
      setInvoiceSearchLoader(true);
      dispatch(getInvoices(invoicePayload)).then((res) => {
        dispatch({
          type: SET_MODAL_DOCUMENTS,
          payload: {
            data: res?.data?.data?.results,
            total: res?.data?.data?.count,
          },
        });
        setInvoiceSearchLoader(false);
      });
    }
  }, [dispatch, invoicePayload, isModalOpen]);

  const validation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,

    initialValues: {
      invoice: "",
    },
    validationSchema: Yup.object({
      invoice: Yup.object().required(`${t("common.please")} ${t("common.select")} ${t("common.invoice")}`),
    }),
    onSubmit: (values) => {
      handleAddDocumentLine(values);
    },
  });

  // handle add/edit document
  const handleAddDocumentLine = (values) => {
    if ((isAllSelected && documentLines?.length === 0) || (!isAllSelected && selectedRows?.length === 0)) {
      toast.info(t("information.atleastOneItem", { module: t("common.documentLine") }));
      return;
    }

    let items = [];
    if (isAllSelected && documentLines?.length) {
      items = getFormattedDocumentLines(
        [
          ...documentLines?.map((row) => ({
            ...row,
            invoice_item_id: row?.id,
            vat: validation?.values?.invoice?.dont_apply_vat ? null : row?.vat,
            premium: row?.premium,
            discount: row?.discount,
            discount_unit: row?.discount_unit,
            positionInContract: row?.auction_lot?.contract_item?.contract_item_no || row?.contract_item?.contract_item_no,
          })),
        ],
        null,
        documentType,
      )?.map((row) => ({
        ...row,
        invoice_id: values?.invoice?.id,
      }));
    }
    if (selectedRows.length > 0 && !isAllSelected) {
      items = getFormattedDocumentLines(
        [
          ...selectedRows?.map((row) => ({
            ...row,
            invoice_item_id: row?.id,
            vat: validation?.values?.invoice?.dont_apply_vat ? null : row?.vat,
            premium: row?.premium,
            discount: row?.discount,
            discount_unit: row?.discount_unit,
            positionInContract: row?.auction_lot?.contract_item?.contract_item_no || row?.contract_item?.contract_item_no,
          })),
        ],
        null,
        documentType,
      )?.map((row) => ({
        ...row,
        invoice_id: values?.invoice?.id,
      }));
    }
    addDocumentLine(items);
  };

  // useEffect to filter the invoices by customer id
  useEffect(() => {
    if (selectedCustomer && isModalOpen) {
      setInvoicePayload((prevState) => ({
        ...prevState,
        filter: { ...prevState?.filter, customer: selectedCustomer },
      }));
    }
  }, [selectedCustomer, isModalOpen]);

  const handleLocalSearch = (data, id, title, description) => {
    let filteredData = data;

    if (id) {
      filteredData = filteredData.filter((item) => String(item?.id)?.includes(id));
    }
    if (title) {
      filteredData = filteredData.filter((item) => item?.item_title?.toLowerCase()?.includes(title));
    }
    if (description) {
      filteredData = filteredData.filter((item) => item?.item_description?.toLowerCase()?.includes(description));
    }

    return filteredData;
  };

  // useEffect to set the document lines according to the selected invoice
  useEffect(() => {
    if (validation?.values?.invoice?.document_lines?.length > 0) {
      setDocumentLines(
        handleLocalSearch(
          getCreditNoteItemsData(validation?.values?.invoice?.document_lines),
          itemsPayload?.filter?.item_id,
          itemsPayload?.filter?.item_title,
          itemsPayload?.filter?.item_description,
        ).filter((lineItem) => {
          if (selectedLots?.length > 0) {
            if (selectedLots?.includes(lineItem?.id)) return false;
          }
          return true;
        }),
      );
    } else {
      setDocumentLines([]);
    }
  }, [validation?.values?.invoice, itemsPayload, selectedLots]);

  // handle reset form with all other maunual states
  const handleReset = () => {
    validation.resetForm();
    setIsAllSelected(false);
    setSelectedRows([]);
  };

  // use effect to reset the modal form
  useEffect(() => {
    if (!isModalOpen) {
      handleReset();
      setItemsPayload({});
    }
  }, [isModalOpen]);

  const columns = [
    {
      name: (
        <div className="font-weight-bold fs-14 table-column-container py-4 d-flex flex-row justify-content-center">
          <Input
            type="checkbox"
            checked={isAllSelected}
            onChange={(event) => {
              setIsAllSelected(event.target.checked);
              setSelectedRows([]);
            }}
          />
        </div>
      ),
      selector: (row) => (
        <Input
          type="checkbox"
          disabled={isAllSelected}
          checked={isAllSelected || Boolean(selectedRows?.find((item) => item?.id === row?.id))}
          onChange={(event) => {
            if (selectedRows?.find((item) => item?.id === row?.id)) {
              setSelectedRows((prevState) => [...prevState?.filter((item) => item?.id !== row?.id)]);
            } else {
              setSelectedRows((prevState) => [...prevState, row]);
            }
          }}
        />
      ),
      width: "10%",
      center: true,
    },
    {
      name: (
        <div className="font-weight-bold fs-14 table-column-container py-4">
          <div className="table-column-title mb-2">{t("common.no_")}</div>
          <CustomSearchInput
            columnWise={true}
            columnSearchKey="item_id"
            className="column-search-input"
            payload={itemsPayload}
            setPayload={setItemsPayload}
          />
        </div>
      ),
      selector: (row) => (row?.id ? row?.id : "-"),
      width: "20%",
    },
    {
      name: (
        <div className="font-weight-bold fs-14 table-column-container py-4">
          <div className="table-column-title mb-2">{t("common.title")}</div>
          <CustomSearchInput
            columnWise={true}
            columnSearchKey={"item_title"}
            className="column-search-input"
            payload={itemsPayload}
            setPayload={setItemsPayload}
          />
        </div>
      ),
      selector: (row) =>
        row?.auction_lot?.id
          ? getColumnDetail(row?.auction_lot?.contract_item?.title_en, row?.auction_lot?.contract_item?.title_pt) || "-"
          : row?.contract_item?.id
            ? getColumnDetail(row?.contract_item?.title_en, row?.contract_item?.title_pt) || "-"
            : row?.product?.id
              ? row?.product?.product_name || "-"
              : "-",
      width: "32.5%",
    },
    {
      name: (
        <div className="font-weight-bold fs-14 table-column-container py-4">
          <div className="table-column-title mb-2">{t("common.description")}</div>
          <CustomSearchInput
            columnWise={true}
            columnSearchKey={"item_description"}
            className="column-search-input"
            payload={itemsPayload}
            setPayload={setItemsPayload}
          />
        </div>
      ),
      selector: (row) =>
        row?.auction_lot?.id
          ? getColumnDetail(
          row?.auction_lot?.contract_item?.description_en,
          row?.auction_lot?.contract_item?.description_pt,
        ) || "-"
          : row?.contract_item?.id
            ? getColumnDetail(row?.contract_item?.description_en, row?.contract_item?.description_pt) || "-"
            : "-",
      width: "32.5%",
      wrap: true,
    },
  ];

  return (
    <Modal
      centered
      isOpen={isModalOpen}
      toggle={handleModalClose}
      fade={false}
      size="lg"
      className="modal-header-width-100"
    >
      <ModalHeader>
        <div className="d-flex w-100 flex-row justify-content-between align-items-center">
          <span>{t("common.addTitle", { module: t("common.documentLine") })}</span>

          {((isAllSelected && documentLines?.length > 0) || selectedRows?.length) && validation?.values?.invoice ? (
            <>
              <Button
                color="info"
                onClick={() => {
                  setSelectedRows([]);
                  setIsAllSelected(false);
                }}
                className="d-flex justify-content-center align-items-center"
              >
                {`${t("common.deselect")}`}
                <Badge color="dark" className="ms-1">
                  {selectedRows?.length || documentLines?.length}
                </Badge>
              </Button>
            </>
          ) : null}
        </div>
      </ModalHeader>
      <Form
        onKeyPress={onKeyPressForm}
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
        action="#"
      >
        <ModalBody>
          {/* form */}
          <Row>
            <Col sm={6} md={6} lg={6}>
              <div className="mb-3">
                <Label htmlFor="invoice" className="form-label">
                  {`${t("common.invoice")}*`}
                </Label>
                <Select
                  isLoading={invoiceSearchLoader}
                  name="invoice"
                  options={INVOICES_OPTIONS}
                  getOptionLabel={getReferenceDocumentLabel}
                  getOptionValue={(option) => option?.id}
                  className={`custom-select ${
                    validation.touched.invoice && validation.errors.invoice ? "select-error" : ""
                  }`}
                  placeholder={`${t("common.select")} ${t("common.invoice")}`}
                  value={validation?.values?.invoice || ""}
                  onInputChange={invoiceSearchDebounce}
                  onChange={(invoice) => {
                    validation.setFieldValue("invoice", invoice);
                    setIsAllSelected(false);
                    setSelectedRows([]);
                  }}
                  onBlur={(e) => {
                    validation.setFieldTouched("invoice", true);
                    validation.handleBlur(e);
                  }}
                />
                {validation.touched.invoice && validation.errors.invoice ? (
                  <p className="custom-invalid-feedback">{validation.errors.invoice}</p>
                ) : null}
              </div>
            </Col>
          </Row>

          {/* lots table */}
          <Card className="subcard-table-container table-light">
            {tableLoader ? <Loader /> : null}
            <DataTable
              className="mini-data-table"
              fixedHeader
              persistTableHead
              columns={columns}
              data={validation?.values?.invoice ? documentLines : []}
              noDataComponent={
                <NoRecords message={!validation?.values?.invoice && t("information.transportDocumentInvoices")} />
              }
              pagination
              paginationComponentOptions={{
                rowsPerPageText: t("common.rowsPerPage"),
                rangeSeparatorText: t("common.rangeSeparator"),
              }}
            />
          </Card>
        </ModalBody>
        <ModalFooter>
          <CustomButton
            type="button"
            color="dark"
            btnTitle={t("common.cancel")}
            onClick={handleModalClose}
            disabled={saveLoader}
          />
          <CustomButton
            type="submit"
            btnTitle={t("common.save")}
            isLoading={saveLoader}
            onClick={() => scrollToFirstFormikError(validation.errors)}
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default withTranslation()(CreditNoteDocumentLineModal);
